/**
 * Customer zod schemas —— 前後端共用。
 * 欄位定義對齊 docs/02-資料字典.md §3.1。
 *
 * 說明：
 * - 長度上限參考 02 §3.1（VARCHAR 大小），對字串做 max 限制。
 * - email 使用 zod email() + 最長 320（RFC 5321）。
 * - tax（統一編號）：臺灣 8 位數字。
 * - website 上限 2083（IE URL 上限；02 已討論）。
 */
import { z } from 'zod';
import { PhoneType } from '../enums/phone-type';

export const CustomerPhoneSchema = z.object({
  id: z.number().int().positive().optional(),
  phoneType: z.nativeEnum(PhoneType),
  displayOrder: z.number().int().nonnegative().default(0),
  number: z.string().trim().min(1).max(30),
  note: z.string().trim().max(100).optional().nullable(),
});

export type CustomerPhone = z.infer<typeof CustomerPhoneSchema>;

/** 建立客戶的輸入 schema（POST /customers body）。 */
export const CreateCustomerSchema = z.object({
  // 客戶編號:非必填,但若有值不可超過 50 字;重複由後端 P2002 回 409。
  cCode: z
    .string()
    .trim()
    .max(50, 'c_code 最長 50 字元')
    .optional()
    .nullable(),
  fullName: z.string().trim().min(1).max(200),
  simpleName: z.string().trim().min(1).max(50),
  tax: z
    .string()
    .trim()
    .regex(/^\d{8}$/, '統一編號需為 8 位數字')
    .optional()
    .nullable(),
  industryId: z.number().int().positive().optional().nullable(),
  contact: z.string().trim().max(50).optional().nullable(),
  position: z.string().trim().max(50).optional().nullable(),
  mobile: z.string().trim().max(30).optional().nullable(),
  email: z.string().trim().email().max(320).optional().nullable(),
  website: z.string().trim().url().max(2083).optional().nullable(),
  address: z.string().trim().max(255).optional().nullable(),
  addressInvoice: z.string().trim().max(255).optional().nullable(),
  paymentCertId: z.number().int().positive().optional().nullable(),
  paymentMethodId: z.number().int().positive().optional().nullable(),
  notes: z.string().max(10000).optional().nullable(),
  // ownerId 在 REQ-auth 之後變為後端自動帶入登入者；仍保留 optional 以允許 ADMIN 顯式指派。
  ownerId: z.number().int().positive().optional(),
  phones: z.array(CustomerPhoneSchema).max(10).default([]),
});

export type CreateCustomerInput = z.infer<typeof CreateCustomerSchema>;

/** 更新：所有欄位 optional（PATCH）。 */
export const UpdateCustomerSchema = CreateCustomerSchema.partial();
export type UpdateCustomerInput = z.infer<typeof UpdateCustomerSchema>;

/** 回傳 Customer 的 shape（API response）。 */
export const CustomerResponseSchema = z.object({
  id: z.number().int().positive(),
  uuid: z.string().uuid(),
  cCode: z.string().nullable(),
  fullName: z.string(),
  simpleName: z.string(),
  tax: z.string().nullable(),
  industryId: z.number().int().nullable(),
  contact: z.string().nullable(),
  position: z.string().nullable(),
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  address: z.string().nullable(),
  addressInvoice: z.string().nullable(),
  paymentCertId: z.number().int().nullable(),
  paymentMethodId: z.number().int().nullable(),
  notes: z.string().nullable(),
  ownerId: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
  phones: z.array(CustomerPhoneSchema).default([]),
});

export type CustomerResponse = z.infer<typeof CustomerResponseSchema>;

/**
 * Customer 操作台 UX 可用的排序 / 搜尋欄位白名單。
 * - CUSTOMER_SEARCHABLE_FIELDS：供「單欄位 LIKE 搜尋」（原系統搜尋 Modal 語意）
 *   `phone` 為虛擬欄位，查詢 customer_phones(phoneType=PHONE) 關聯表的 3 個槽。
 *   其餘欄位為 Customer 表本體欄位。
 * - CUSTOMER_SORTABLE_FIELDS：供 sort / neighbor 翻頁排序，僅 Customer 表本體欄位。
 *   關聯虛擬欄位（如 phone）不能用於翻頁排序。
 */
export const CUSTOMER_SEARCHABLE_FIELDS = [
  'phone',
  'simpleName',
  'cCode',
  'fullName',
  'tax',
  'contact',
  'mobile',
  'email',
  'address',
] as const;
export type CustomerSearchableField = (typeof CUSTOMER_SEARCHABLE_FIELDS)[number];

export const CUSTOMER_SORTABLE_FIELDS = [
  'cCode',
  'simpleName',
  'fullName',
  'tax',
  'contact',
  'mobile',
  'email',
  'address',
  'id',
  'createdAt',
  'updatedAt',
] as const;
export type CustomerSortableField = (typeof CUSTOMER_SORTABLE_FIELDS)[number];

/** 列表查詢參數。對齊 05 §9.3。 */
export const CustomerListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
  q: z.string().trim().max(100).optional(),
  industryId: z.coerce.number().int().positive().optional(),
  ownerId: z.coerce.number().int().positive().optional(),
  /**
   * 單欄位搜尋模式：若帶 field + q，僅對該欄位做 LIKE（對齊原系統搜尋 Modal）。
   * 不帶 field 時沿用 q 的多欄位 OR 搜尋行為。
   */
  field: z.enum(CUSTOMER_SEARCHABLE_FIELDS).optional(),
  sort: z.string().optional().default('updatedAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
});

export type CustomerListQuery = z.infer<typeof CustomerListQuerySchema>;

/**
 * Neighbor 查詢（上一筆 / 下一筆）。
 * REQ-20260421009 §1:加入 scoped paging,field + q 必須成對出現或同時省略。
 */
export const CustomerNeighborQuerySchema = z
  .object({
    direction: z.enum(['next', 'previous']),
    sortBy: z.enum(CUSTOMER_SORTABLE_FIELDS).default('cCode'),
    order: z.enum(['asc', 'desc']).default('asc'),
    field: z.enum(CUSTOMER_SEARCHABLE_FIELDS).optional(),
    q: z.string().trim().max(100).optional(),
  })
  .refine((v) => (v.field == null) === (v.q == null), {
    message: 'field 與 q 必須同時提供或同時省略',
    path: ['field'],
  });

export type CustomerNeighborQuery = z.infer<typeof CustomerNeighborQuerySchema>;

/**
 * 客戶編號自動流水號查詢（REQ-20260912001）。
 * 使用者自己 key 前 3 碼分類碼，後端回同前綴下一個可用的 6 碼流水號。
 */
export const CustomerNextCodeQuerySchema = z.object({
  prefix: z.string().trim().regex(/^\d{3}$/, '前綴需為 3 位數字'),
});

export type CustomerNextCodeQuery = z.infer<typeof CustomerNextCodeQuerySchema>;
